import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, List, Play, ChevronLeft, ChevronRight, User, LogOut, Radio } from 'lucide-react';
import { Button, Dropdown } from 'react-bootstrap';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../Context/AuthContext';
import { useContest } from '../Context/ContestContext';
import Swal from 'sweetalert2';

const AdminLayout = () => {
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const { roomCode, gameState, terminateGame, isHost } = useContest();

  // Auto-collapse on smaller screens
  React.useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 992) {
        setCollapsed(true);
      } else {
        setCollapsed(false);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  // Oyun yönetim sayfaları — bu sayfalara geçişte "sonlandır" uyarısı çıkmasın
  const isGameManagementPath = (path) => path === '/lobby' || path === '/contest';

  const handleNavigation = (e, path) => {
    // Oyun sayfalarına geçişte uyarı gösterme
    if (isGameManagementPath(path)) return;

    // Sadece bu odanın hostu ise uyarı göster — diğer adminler sadece izleyici
    if (roomCode && isHost) {
      e.preventDefault();
      Swal.fire({
        title: 'Yarışmayı sonlandırmak istediğinize emin misiniz?',
        text: 'Bu sayfadan ayrılırsanız mevcut yarışma sonlandırılacak ve tüm oyuncular atılacaktır!',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#ef4444',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Evet, Sonlandır',
        cancelButtonText: 'Vazgeç',
      }).then((result) => {
        if (result.isConfirmed) {
          terminateGame();
          navigate(path);
        }
      });
    }
  };

  // Aktif oyuna dön
  const handleReturnToGame = () => {
    if (gameState === 'LOBBY') {
      navigate('/lobby');
    } else {
      navigate('/contest');
    }
  };

  const navItems = [
    { path: '/', icon: LayoutDashboard, label: 'Panel' },
    { path: '/games', icon: Play, label: 'Oyunlar' },
    { path: '/questions', icon: List, label: 'Soru Havuzu' },
  ];

  return (
    <div className="app-wrapper">
      {/* Sidebar */}
      <aside className={`sidebar-wrapper ${collapsed ? 'collapsed' : ''}`}>
        {/* Sidebar Header */}
        <div
          className="d-flex align-items-center justify-content-center p-4"
          style={{ height: '80px', borderBottom: '1px solid #e2e8f0' }}
        >
          {!collapsed ? (
            <motion.h4
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="m-0 fw-bold text-gradient font-monospace"
            >
              UNIVERSAL ARENA
            </motion.h4>
          ) : (
            <h4 className="m-0 fw-bold text-gradient">U</h4>
          )}
        </div>

        {/* Navigation */}
        <nav className="nav-links flex-grow-1 mt-3">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`nav-item-custom ${isActive(item.path) ? 'active' : ''}`}
              onClick={(e) => handleNavigation(e, item.path)}
            >
              <div className="nav-icon">
                <item.icon size={22} />
              </div>
              {!collapsed && (
                <motion.span
                  initial={{ opacity: 0, x: -5 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="nav-label"
                >
                  {item.label}
                </motion.span>
              )}
            </Link>
          ))}

          {/* AKTİF OYUN BANNER */}
          <AnimatePresence>
            {roomCode && (
              <motion.div
                key="active-game-banner"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                onClick={handleReturnToGame}
                title={`Aktif Oyun: ${roomCode} — Tıkla ve Devam Et`}
                style={{
                  padding: collapsed ? '10px' : '10px 14px',
                  margin: '10px 10px 0',
                  borderRadius: 10,
                  background: 'linear-gradient(135deg, #16a34a, #15803d)',
                  cursor: 'pointer',
                  boxShadow: '0 2px 12px rgba(22,163,74,0.4)',
                }}
              >
                <div className="d-flex align-items-center gap-2">
                  <motion.div
                    animate={{ scale: [1, 1.35, 1] }}
                    transition={{ duration: 1.2, repeat: Infinity }}
                  >
                    <Radio size={18} color="white" />
                  </motion.div>
                  {!collapsed && (
                    <div>
                      <div style={{ color: 'white', fontWeight: 700, fontSize: 12, lineHeight: 1 }}>
                        AKTİF OYUN
                      </div>
                      <div style={{ color: '#bbf7d0', fontSize: 11, marginTop: 3 }}>
                        PIN: {roomCode} — Devam Et →
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </nav>

        {/* Logout Button (Fixed Bottom) */}
        <div className="p-3 mt-auto border-top border-light">
          <Button
            variant="danger"
            className={`w-100 d-flex align-items-center ${
              collapsed ? 'justify-content-center p-2' : 'justify-content-start px-3'
            }`}
            onClick={logout}
            title="Çıkış Yap"
          >
            <LogOut size={20} />
            {!collapsed && <span className="ms-2">Çıkış Yap</span>}
          </Button>
        </div>
      </aside>

      {/* Main Content Wrapper */}
      <main className="content-wrapper p-0 d-flex flex-column">
        {/* Top Navbar */}
        <header
          className="bg-white border-bottom d-flex align-items-center justify-content-between px-4"
          style={{ height: '80px' }}
        >
          {/* Left: Toggle & Breadcrumb */}
          <div className="d-flex align-items-center gap-3">
            <Button
              variant="light"
              size="sm"
              className="border-0 p-1"
              onClick={() => setCollapsed(!collapsed)}
            >
              {collapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
            </Button>

            <div className="d-none d-md-block">
              <Link
                to="/"
                onClick={(e) => handleNavigation(e, '/')}
                className="text-secondary small fw-bold text-decoration-none"
              >
                Ana Sayfa
              </Link>
              <span className="mx-2 text-muted">/</span>
              <span className="text-primary fw-bold">
                {navItems.find((i) => isActive(i.path))?.label || 'Sayfa'}
              </span>
            </div>
          </div>

          {/* Right: User Profile */}
          <Dropdown align="end">
            <Dropdown.Toggle
              as="div"
              className="d-flex align-items-center gap-3"
              style={{ cursor: 'pointer' }}
            >
              <div className="text-end d-none d-sm-block">
                <p className="m-0 fw-bold text-dark small">
                  {user?.isim ? `${user.isim} ${user.soyisim}` : 'Yükleniyor...'}
                </p>
                <p className="m-0 text-muted" style={{ fontSize: '10px' }}>
                  {user?.isAdmin
                    ? 'Yönetici (Admin)'
                    : (user?.kariyer || (user?.isYetkili ? 'Yetkili' : 'Kullanıcı'))}
                </p>
              </div>
              <div
                className="rounded-circle bg-light d-flex align-items-center justify-content-center border"
                style={{ width: 40, height: 40 }}
              >
                <User size={20} className="text-primary" />
              </div>
            </Dropdown.Toggle>

            <Dropdown.Menu className="border-0 shadow-lg mt-2">
              <Dropdown.Item
                onClick={logout}
                className="text-danger small fw-bold d-flex align-items-center gap-2"
              >
                <LogOut size={16} />
                Çıkış Yap
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </header>

        {/* Page Content */}
        <div className="p-4 flex-grow-1 overflow-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
